import React, { useEffect, useState } from 'react'
import UpgradeSuccessModal from 'components/upgrade-success-modal'
import { useUpgradeFlow } from 'lib/hooks/use-upgrade-flow'
import { amplitudeEvent } from 'lib/amplitude'
import { useSnackbar, useSnackbarError, useSnackbarSuccess } from 'lib/snackbar'
import { useUpdateOutreachTemplate, useUpsertOutreachRule } from './api-hooks'
import ApprovalDialog from './approval-dialog'
import EditRuleDialog from './edit-rule-dialog'
import { PAGE_NAME, describeRule } from './constants'

// Turning a rule on, editing its message and timing, and the Coach+ upgrade
// all run through here. `data` is the `useFollowUpAutomations` result; render
// `dialogs` next to the rows.
export default function useAutomationActions({ data, refetch }) {
  const { enqueueSnackbar } = useSnackbar()
  const snackbarSuccess = useSnackbarSuccess(enqueueSnackbar)
  const snackbarError = useSnackbarError(enqueueSnackbar)
  const [upsertRule] = useUpsertOutreachRule()
  const [updateTemplate] = useUpdateOutreachTemplate()
  const {
    showSuccessModal,
    upgradeLoading,
    handleUpgradeClick,
    handleDismiss,
    trackPromptShown,
  } = useUpgradeFlow(PAGE_NAME, { trackPromptShown: false })
  const [approving, setApproving] = useState(null)
  const [editing, setEditing] = useState(null)
  const [savingTrigger, setSavingTrigger] = useState(null)
  const [dialogError, setDialogError] = useState(null)
  const automations = data?.outreachAutomations
  const available = automations?.available

  useEffect(() => {
    if (available === false) trackPromptShown()
  }, [available, trackPromptShown])

  const closeDialogs = () => {
    setApproving(null)
    setEditing(null)
    setDialogError(null)
  }

  const withSaving = async (trigger, save) => {
    setSavingTrigger(trigger)
    try {
      await save()
    } finally {
      setSavingTrigger(null)
    }
  }

  const saveRule = async (trigger, variables) => {
    const { data: result } = await upsertRule({
      variables: { trigger, ...variables },
    })
    return result.upsertOutreachRule
  }

  const onToggle = async (rule, checked) => {
    if (checked) {
      setDialogError(null)
      setApproving(rule)
      return
    }
    await withSaving(rule.trigger, async () => {
      try {
        const response = await saveRule(rule.trigger, { enabled: false })
        if (!response.success) return snackbarError(response.message)
        amplitudeEvent('follow-up-automation-disabled', {
          trigger: rule.trigger,
        })
        snackbarSuccess(response.message)
        await refetch()
      } catch (e) {
        snackbarError(e.message)
      }
    })
  }

  const handleApprove = () =>
    withSaving(approving.trigger, async () => {
      try {
        const response = await saveRule(approving.trigger, {
          enabled: true,
          delayDays: approving.delayDays,
        })
        if (!response.success) return setDialogError(response.message)
        amplitudeEvent('follow-up-automation-enabled', {
          trigger: approving.trigger,
          delayDays: approving.delayDays,
        })
        snackbarSuccess(
          approving.previousDelayDays == null
            ? response.message
            : 'New timing saved',
        )
        closeDialogs()
        await refetch()
      } catch (e) {
        setDialogError(e.message)
      }
    })

  const handleCancelApproval = () => {
    amplitudeEvent('follow-up-automation-preview-canceled', {
      trigger: approving.trigger,
    })
    if (approving.previousDelayDays != null) {
      enqueueSnackbar(
        `${
          approving.messageSaved ? 'Message saved. ' : ''
        }Timing not changed. ${describeRule(
          approving.trigger,
          approving.previousDelayDays,
        )}`,
        { variant: 'info' },
      )
    }
    closeDialogs()
  }

  const templateFor = trigger =>
    data.outreachTemplates.find(template => template.trigger === trigger)

  const handleSave = ({ delayDays, body }) => {
    const { trigger } = editing
    const template = templateFor(trigger)
    return withSaving(trigger, async () => {
      try {
        if (body !== template.body) {
          const { data: result } = await updateTemplate({
            variables: {
              trigger,
              body: body === template.defaultBody ? '' : body,
            },
          })
          if (!result.updateOutreachTemplate.success) {
            return setDialogError(result.updateOutreachTemplate.message)
          }
        }
        const delayChanged = delayDays !== editing.delayDays
        if (delayChanged && !editing.active) {
          const response = await saveRule(trigger, { delayDays })
          if (!response.success) {
            refetch()
            return setDialogError(response.message)
          }
        }
        amplitudeEvent('follow-up-automation-saved', {
          trigger,
          messageChanged: body !== template.body,
          delayChanged: delayChanged && !editing.active,
        })
        if (delayChanged && editing.active) {
          setEditing(null)
          setDialogError(null)
          setApproving({
            ...editing,
            delayDays,
            previousDelayDays: editing.delayDays,
            messageSaved: body !== template.body,
          })
        } else {
          snackbarSuccess('Automation saved')
          closeDialogs()
        }
        await refetch()
      } catch (e) {
        setDialogError(e.message)
      }
    })
  }

  const onEdit = rule => {
    setDialogError(null)
    setEditing(rule)
  }

  const dialogs = (
    <>
      {approving ? (
        <ApprovalDialog
          rule={approving}
          sendSchedule={automations.sendSchedule}
          holdoutPercent={automations.holdoutPercent}
          saving={savingTrigger === approving.trigger}
          error={dialogError}
          onCancel={handleCancelApproval}
          onConfirm={handleApprove}
        />
      ) : null}
      {editing ? (
        <EditRuleDialog
          rule={editing}
          template={templateFor(editing.trigger)}
          saving={savingTrigger === editing.trigger}
          error={dialogError}
          onCancel={closeDialogs}
          onSave={handleSave}
        />
      ) : null}
      <UpgradeSuccessModal isOpen={showSuccessModal} onClose={handleDismiss} />
    </>
  )

  return {
    savingTrigger,
    upgradeLoading,
    onToggle,
    onEdit,
    onUpgrade: handleUpgradeClick,
    dialogs,
  }
}
